import { httpClient } from './http';
import { API_ENDPOINTS } from '../config/api';

export interface OrderItem {
  id: string;
  name: string;
  quantity: number;
  price: number;
  specialInstructions?: string;
  notes?: string;
  size?: string;
}

export interface Order {
  id: string;
  tableNumber: string;
  waiterName?: string;
  items: OrderItem[];
  totalAmount: number;
  status: 'created' | 'pending' | 'confirmed' | 'preparing' | 'ready' | 'delivered' | 'cancelled' | 'completed' | 'paid';
  createdAt: string;
  updatedAt: string;
  notes?: string;
  specialNotes?: string;
  estimatedTime?: number;
  assignedWaiters?: Array<{id: string; name: string}>;
}

// Interfaz para datos del backend que pueden usar snake_case
interface BackendOrder {
  id: string;
  tableNumber?: string;
  table_number?: string;
  waiterName?: string;
  waiter_name?: string;
  items?: any[];
  totalAmount?: number;
  total_amount?: number;
  status?: string;
  createdAt?: string;
  created_at?: string;
  updatedAt?: string;
  updated_at?: string;
  notes?: string;
  specialNotes?: string;
  special_notes?: string;
  deliveryAddress?: string | null;
  delivery_address?: string | null;
  estimatedTime?: number;
  estimated_time?: number;
  assignedWaiters?: Array<{id: string; name: string}>;
  assigned_waiters?: Array<{id: string; name: string}>;
}

// Interfaz para la respuesta paginada del backend
interface BackendResponse {
  data: BackendOrder[];
  pagination: {
    page: number;
    size: number;
    total: string;
    pages: number;
  };
}

export interface OrdersPage {
  data: Order[];
  pagination: {
    page: number;
    size: number;
    total: number;
    pages: number;
  };
}

function mapBackendOrder(order: BackendOrder): Order {
  const items = (order.items || []).map((item: any) => ({
    id: item.id,
    name: item.name || item.menuItemName || item.menu_item_name || 'Producto',
    quantity: item.quantity || 1,
    price: parseFloat(item.price || item.unitPrice || item.unit_price || 0),
    specialInstructions: item.specialInstructions || item.special_instructions,
    notes: item.notes,
    size: item.size,
  }));
  const computedTotal = items.reduce((sum, it) => sum + (Number(it.price) || 0) * (Number(it.quantity) || 0), 0);
  const parsedTotal = parseFloat(String(order.totalAmount || order.total_amount || 0));
  const totalAmount = Number.isFinite(parsedTotal) && parsedTotal > 0 ? parsedTotal : computedTotal;
  const deliveryAddressRaw = order.deliveryAddress || order.delivery_address || null;
  const deliveryAddress =
    typeof deliveryAddressRaw === 'string' && deliveryAddressRaw.trim().toLowerCase() === 'local'
      ? null
      : deliveryAddressRaw;
  const tableNumberStr = order.tableNumber ?? order.table_number ?? '';
  const isVirtualTableZero = parseInt(String(tableNumberStr), 10) === 0;
  const specialNotesRaw = order.specialNotes || order.special_notes || null;
  const specialNotes = isVirtualTableZero
    ? (specialNotesRaw || order.notes || deliveryAddress)
    : (specialNotesRaw || deliveryAddress || order.notes);
  const specialNotesOut = typeof specialNotes === 'string' ? specialNotes : undefined;
  const tableNumberOutRaw = order.tableNumber ?? order.table_number;
  const tableNumberOut =
    tableNumberOutRaw === undefined || tableNumberOutRaw === null || tableNumberOutRaw === ''
      ? '1'
      : String(tableNumberOutRaw);
  return {
    id: order.id,
    tableNumber: tableNumberOut,
    waiterName: order.waiterName || order.waiter_name,
    items,
    totalAmount,
    status: (order.status || 'pending') as Order['status'],
    createdAt: order.createdAt || order.created_at || new Date().toISOString(),
    updatedAt: order.updatedAt || order.updated_at || new Date().toISOString(),
    notes: order.notes,
    specialNotes: specialNotesOut,
    estimatedTime: order.estimatedTime || order.estimated_time || 15,
    assignedWaiters: order.assignedWaiters || order.assigned_waiters,
  };
}
export interface UpdateOrderStatusData {
  status: Order['status'];
}

// Servicio para gestión de órdenes en admin
export const adminOrdersService = {
  // Listado paginado con filtros (status, fechas)
  listPaged: async (
    userId: string,
    restaurantId: string,
    params: {
      page?: number;
      size?: number;
      status?: string;
      from?: string; // ISO (yyyy-mm-dd) o fecha completa
      to?: string;   // ISO
    } = {}
  ): Promise<OrdersPage> => {
    const endpoint = API_ENDPOINTS.ORDERS(userId, restaurantId);
    const query: Record<string, string | undefined> = {
      page: params.page ? String(params.page) : undefined,
      size: params.size ? String(params.size) : undefined,
      status: params.status,
      from: params.from,
      to: params.to,
    };
    const response = await httpClient.get<BackendResponse>(endpoint, query, 'authenticated');
    const totalNum = parseInt(String(response.pagination?.total || 0), 10) || 0;
    return {
      data: (response.data || []).map(mapBackendOrder),
      pagination: {
        page: response.pagination?.page || 1,
        size: response.pagination?.size || (params.size || 10),
        total: totalNum,
        pages: response.pagination?.pages || Math.ceil(totalNum / (params.size || 10)),
      },
    };
  },
  // Obtener una orden por ID (incluye items)
  getOrderById: async (userId: string, restaurantId: string, orderId: string): Promise<Order | null> => {
    try {
      const endpoint = API_ENDPOINTS.ORDER_BY_ID(userId, restaurantId, orderId);
      const response = await httpClient.get<BackendOrder>(endpoint, undefined, 'authenticated');
      if (!response) return null;
      const items = (response.items || []).map((item: any) => ({
        id: item.id,
        name: item.name || item.menuItemName || item.menu_item_name || 'Producto',
        quantity: item.quantity || 1,
        price: parseFloat(item.price || item.unitPrice || item.unit_price || 0),
        specialInstructions: item.specialInstructions || item.special_instructions,
        notes: item.notes,
        size: item.size || 'normal',
      }));
      const computedTotal = items.reduce((sum, it) => sum + (Number(it.price) || 0) * (Number(it.quantity) || 0), 0);
      const parsedTotal = parseFloat(String(response.totalAmount || response.total_amount || 0));
      const totalAmount = Number.isFinite(parsedTotal) && parsedTotal > 0 ? parsedTotal : computedTotal;
      const tableNumberOutRaw = response.tableNumber ?? response.table_number;
      const tableNumberOut =
        tableNumberOutRaw === undefined || tableNumberOutRaw === null || tableNumberOutRaw === ''
          ? '1'
          : String(tableNumberOutRaw);
      const specialNotesOutRaw = response.specialNotes ?? response.special_notes;
      const specialNotesOut = specialNotesOutRaw == null ? undefined : String(specialNotesOutRaw);
      return {
        id: response.id,
        tableNumber: tableNumberOut,
        waiterName: response.waiterName || response.waiter_name,
        items,
        totalAmount,
        status: (response.status || 'pending') as Order['status'],
        createdAt: response.createdAt || response.created_at || new Date().toISOString(),
        updatedAt: response.updatedAt || response.updated_at || new Date().toISOString(),
        notes: response.notes,
        specialNotes: specialNotesOut,
        estimatedTime: response.estimatedTime || response.estimated_time || 15,
        assignedWaiters: response.assignedWaiters || response.assigned_waiters,
      };
    } catch (error) {
      console.error('❌ Error obteniendo orden por ID:', error);
      return null;
    }
  },
  // Obtener todas las órdenes de un restaurante
  getOrdersByRestaurant: async (userId: string, restaurantId: string, status?: string): Promise<Order[]> => {
    try {
      console.log('📋 AdminOrdersService - Obteniendo órdenes para restaurante:', restaurantId, 'estado:', status);
      
      let endpoint = API_ENDPOINTS.ORDERS(userId, restaurantId);
      if (status) {
        endpoint += `?status=${status}`;
      }
      
      const response = await httpClient.get<BackendResponse>(
        endpoint,
        undefined,
        'authenticated'
      );
      
      console.log('📋 AdminOrdersService - Respuesta del backend:', response);
      
      // Manejar respuesta paginada del backend
      if (response && response.data && Array.isArray(response.data)) {
        return response.data.map((order: BackendOrder) => {
          const deliveryAddressRaw = order.deliveryAddress || order.delivery_address || null;
          const deliveryAddress =
            typeof deliveryAddressRaw === 'string' && deliveryAddressRaw.trim().toLowerCase() === 'local'
              ? null
              : deliveryAddressRaw;
          const tableNumberStr = order.tableNumber ?? order.table_number ?? '';
          const isVirtualTableZero = parseInt(String(tableNumberStr), 10) === 0;
          const specialNotesRaw = order.specialNotes || order.special_notes || null;
          const specialNotes = isVirtualTableZero
            ? (specialNotesRaw || order.notes || deliveryAddress)
            : (specialNotesRaw || deliveryAddress || order.notes);
          const specialNotesOut = typeof specialNotes === 'string' ? specialNotes : undefined;
          const items = (order.items || []).map((item: any) => ({
            id: item.id,
            name: item.name || item.menuItemName || item.menu_item_name || 'Producto',
            quantity: item.quantity || 1,
            price: parseFloat(item.price || item.unitPrice || item.unit_price || 0),
            specialInstructions: item.specialInstructions || item.special_instructions,
            notes: item.notes,
            size: item.size,
          }));
          const computedTotal = items.reduce((sum, it) => sum + (Number(it.price) || 0) * (Number(it.quantity) || 0), 0);
          const parsedTotal = parseFloat(String(order.totalAmount || order.total_amount || 0));
          const totalAmount = Number.isFinite(parsedTotal) && parsedTotal > 0 ? parsedTotal : computedTotal;
          const tableNumberOutRaw = order.tableNumber ?? order.table_number;
          const tableNumberOut =
            tableNumberOutRaw === undefined || tableNumberOutRaw === null || tableNumberOutRaw === ''
              ? '1'
              : String(tableNumberOutRaw);
          return {
            id: order.id,
            tableNumber: tableNumberOut,
            waiterName: order.waiterName || order.waiter_name,
            items,
            totalAmount,
            status: (order.status || 'pending') as Order['status'],
            createdAt: order.createdAt || order.created_at || new Date().toISOString(),
            updatedAt: order.updatedAt || order.updated_at || new Date().toISOString(),
            notes: order.notes,
            specialNotes: specialNotesOut,
            estimatedTime: order.estimatedTime || order.estimated_time || 15,
            assignedWaiters: order.assignedWaiters || order.assigned_waiters,
          };
        });
      }
      
      // Fallback para respuesta directa (sin paginación)
      if (Array.isArray(response)) {
        return response.map((order: BackendOrder) => {
          const deliveryAddressRaw = order.deliveryAddress || order.delivery_address || null;
          const deliveryAddress =
            typeof deliveryAddressRaw === 'string' && deliveryAddressRaw.trim().toLowerCase() === 'local'
              ? null
              : deliveryAddressRaw;
          const tableNumberStr = order.tableNumber ?? order.table_number ?? '';
          const isVirtualTableZero = parseInt(String(tableNumberStr), 10) === 0;
          const specialNotesRaw = order.specialNotes || order.special_notes || null;
          const specialNotes = isVirtualTableZero
            ? (specialNotesRaw || order.notes || deliveryAddress)
            : (specialNotesRaw || deliveryAddress || order.notes);
          const specialNotesOut = typeof specialNotes === 'string' ? specialNotes : undefined;
          const items = (order.items || []).map((item: any) => ({
            id: item.id,
            name: item.name || item.menuItemName || item.menu_item_name || 'Producto',
            quantity: item.quantity || 1,
            price: parseFloat(item.price || item.unitPrice || item.unit_price || 0),
            specialInstructions: item.specialInstructions || item.special_instructions,
            notes: item.notes,
            size: item.size,
          }));
          const computedTotal = items.reduce((sum, it) => sum + (Number(it.price) || 0) * (Number(it.quantity) || 0), 0);
          const parsedTotal = parseFloat(String(order.totalAmount || order.total_amount || 0));
          const totalAmount = Number.isFinite(parsedTotal) && parsedTotal > 0 ? parsedTotal : computedTotal;
          const tableNumberOutRaw = order.tableNumber ?? order.table_number;
          const tableNumberOut =
            tableNumberOutRaw === undefined || tableNumberOutRaw === null || tableNumberOutRaw === ''
              ? '1'
              : String(tableNumberOutRaw);
          return {
            id: order.id,
            tableNumber: tableNumberOut,
            waiterName: order.waiterName || order.waiter_name,
            items,
            totalAmount,
            status: (order.status || 'pending') as Order['status'],
            createdAt: order.createdAt || order.created_at || new Date().toISOString(),
            updatedAt: order.updatedAt || order.updated_at || new Date().toISOString(),
            notes: order.notes,
            specialNotes: specialNotesOut,
            estimatedTime: order.estimatedTime || order.estimated_time || 15,
            assignedWaiters: order.assignedWaiters || order.assigned_waiters,
          };
        });
      }
      
      console.error('❌ AdminOrdersService - Respuesta no válida:', typeof response, response);
      return [];
    } catch (error) {
      console.error('❌ Error obteniendo órdenes:', error);
      // Propagar el error para que la UI muestre el ErrorModal
      throw error;
    }
  },

  // Actualizar estado de una orden
  updateOrderStatus: async (userId: string, restaurantId: string, orderId: string, statusData: UpdateOrderStatusData): Promise<Order | null> => {
    try {
      console.log('📋 AdminOrdersService - Actualizando estado de orden:', orderId, statusData);
      
      const response = await httpClient.put<BackendOrder>(
        `${API_ENDPOINTS.ORDERS(userId, restaurantId)}/${orderId}/status`,
        statusData,
        'authenticated'
      );
      
      console.log('📋 AdminOrdersService - Orden actualizada:', response);
      
      const items = (response.items || []).map((item: any) => ({
        id: item.id,
        name: item.name || item.menuItemName || item.menu_item_name || 'Producto',
        quantity: item.quantity || 1,
        price: parseFloat(item.price || item.unitPrice || item.unit_price || 0),
        specialInstructions: item.specialInstructions || item.special_instructions,
        notes: item.notes,
        size: item.size || item.variant || item.tamano,
      }));
      const computedTotal = items.reduce((sum, it) => sum + (Number(it.price) || 0) * (Number(it.quantity) || 0), 0);
      const parsedTotal = parseFloat(String(response.totalAmount || response.total_amount || 0));
      const totalAmount = Number.isFinite(parsedTotal) && parsedTotal > 0 ? parsedTotal : computedTotal;
      const tableNumberOutRaw = response.tableNumber ?? response.table_number;
      const tableNumberOut =
        tableNumberOutRaw === undefined || tableNumberOutRaw === null || tableNumberOutRaw === ''
          ? '1'
          : String(tableNumberOutRaw);
      const specialNotesOutRaw = response.specialNotes ?? response.special_notes;
      const specialNotesOut = specialNotesOutRaw == null ? undefined : String(specialNotesOutRaw);
      return {
        id: response.id,
        tableNumber: tableNumberOut,
        waiterName: response.waiterName || response.waiter_name,
        items,
        totalAmount,
        status: (response.status || 'pending') as Order['status'],
        createdAt: response.createdAt || response.created_at || new Date().toISOString(),
        updatedAt: response.updatedAt || response.updated_at || new Date().toISOString(),
        notes: response.notes,
        specialNotes: specialNotesOut,
        estimatedTime: response.estimatedTime || response.estimated_time || 15,
        assignedWaiters: response.assignedWaiters || response.assigned_waiters,
      };
    } catch (error) {
      console.error('❌ Error actualizando estado de orden:', error);
      // Re-lanzar para que la UI pueda mostrar el mensaje exacto del backend (e.g., 403 con message)
      throw error;
    }
  },

  // Obtener estadísticas de órdenes
  getOrderStats: async (userId: string, restaurantId: string): Promise<any> => {
    try {
      console.log('📋 AdminOrdersService - Obteniendo estadísticas de órdenes para restaurante:', restaurantId);
      
      const response = await httpClient.get<any>(
        `${API_ENDPOINTS.ORDERS(userId, restaurantId)}/stats`,
        undefined,
        'authenticated'
      );
      
      console.log('📋 AdminOrdersService - Estadísticas obtenidas:', response);
      return response;
    } catch (error) {
      console.error('❌ Error obteniendo estadísticas de órdenes:', error);
      throw error;
    }
  },

  // Cancelar una orden
  cancelOrder: async (userId: string, restaurantId: string, orderId: string): Promise<boolean> => {
    try {
      console.log('📋 AdminOrdersService - Cancelando orden:', orderId);
      
      await httpClient.patch<BackendOrder>(
        `${API_ENDPOINTS.ORDERS(userId, restaurantId)}/${orderId}/status`,
        { status: 'cancelled' },
        'authenticated'
      );
      
      console.log('📋 AdminOrdersService - Orden cancelada exitosamente');
      return true;
    } catch (error) {
      console.error('❌ Error cancelando orden:', error);
      throw error;
    }
  },
}; 